import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Rss, MessageSquare } from "lucide-react";
import { AppHeader, SourceNote } from "@/components/Layout";
import { useSeo } from "@/lib/seo";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const PRINCIPLES = [
  {
    Icon: Rss,
    title: "One source, and it is the official one",
    body: "Everything comes from the Food Standards Australia New Zealand recall feed. No aggregated blogs, no social media rumours, no guesses. If FSANZ has not published it, we do not send it.",
  },
  {
    Icon: MessageSquare,
    title: "A text, because a text gets read",
    body: "No app to install, no notification buried in a settings menu, no email sitting unread under receipts. A text arrives on any phone, with or without data, and people actually look at it.",
  },
  {
    Icon: Clock,
    title: "Short enough to act on immediately",
    body: "Each alert names the product, the risk and where it was sold, then links to the official notice. Everything you need to check your own cupboard while you are still standing in front of it.",
  },
];

export default function About() {
  useSeo({
    title: "Our story | Why we built Food Recall Alerts Australia",
    description:
      "Why an independent Australian food recall alert service exists: official FSANZ recalls are published, but the warning rarely reaches the kitchen in time. Read the story behind Food Recall Alerts.",
    path: "/about",
  });
  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="pointer-events-none absolute -left-32 -top-32 h-[26rem] w-[26rem] rounded-full bg-red-50 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-5 py-12 sm:py-16 lg:py-24">
          <motion.div initial="hidden" animate="show">
            <motion.p variants={fadeUp} custom={0} className="label-caps">Why this exists</motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="mt-4 font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl"
            >
              It started with a jar that had already been recalled.
            </motion.h1>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-2xl px-5 py-12 sm:py-16 lg:py-20">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="space-y-6 text-lg leading-relaxed text-slate-700"
            data-testid="about-story"
          >
            <p>
              A close family friend of ours has a peanut allergy. The serious kind, the kind where an
              adrenaline injector lives in her handbag and eating anything she has not made herself
              involves a conversation with the kitchen first. She has been doing this her whole life. She
              is careful in a way most of us never have to be.
            </p>
            <p>
              One evening she was at ours for dinner. There was a jar in the cupboard, something we had
              bought a few weeks earlier, and it was going into the sauce. She did what she always does and
              read the label. Nothing listed. No warning, no <em>may contain</em>, nothing.
            </p>
            <p className="font-display text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              That product had been recalled eleven days earlier for undeclared peanut.
            </p>
            <p>
              We found out later that night, on a phone, scrolling. The notice had been sitting on the
              Food Standards Australia New Zealand website the whole time, published properly, exactly as
              the system is meant to work. It had simply never reached the two people in that kitchen who
              needed it.
            </p>
            <p>
              Nothing happened to her that night. We got lucky, and it is worth saying plainly that luck
              is what it was. But the thing that stayed with us was not the near miss. It was how
              ordinary the failure had been. Nobody did anything wrong. The recall was issued, the label
              was read, the shelf was checked. And the information still did not travel the last few
              metres to the person holding the jar.
            </p>
            <p>
              That is the whole problem. Recalls in Australia are published well. They are just published
              to a website, and websites need you to remember to visit them. Meanwhile the product is
              already home, already in the pantry, already halfway into dinner.
            </p>
            <p>
              So we built the missing step. Something that watches the official feed constantly, works out
              whether a recall actually applies to you, and then sends it to the one place you will
              definitely see it: a text message on your phone.
            </p>
            <p>
              It is deliberately unexciting. No feed to scroll, no app to open, no dashboard to check.
              Most days you hear nothing from us at all. Then one day a product you have in the cupboard
              gets recalled, and you know about it in minutes instead of eleven days later.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-5xl px-5 py-12 sm:py-16 lg:py-20">
          <h2 className="font-display text-3xl font-extrabold tracking-tighter text-slate-900 sm:text-4xl">
            How we think about it
          </h2>
          <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="about-principles">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-3xl border border-slate-200 bg-white p-6 transition-shadow sm:p-7 duration-300 hover:shadow-xl"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                  <p.Icon className="h-5 w-5 text-red-700" />
                </span>
                <h3 className="mt-5 font-display text-base font-semibold tracking-tight text-slate-900 md:text-lg">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-2xl px-5 py-12 text-center sm:py-16 lg:py-20">
          <h2 className="font-display text-2xl font-extrabold tracking-tighter text-slate-900 sm:text-3xl">
            Being honest about what this is
          </h2>
          <p className="mt-5 text-slate-600">
            We are an independent service, not part of FSANZ or any government body, and we do not decide
            what gets recalled. We pass on official notices faster and more personally than a website can.
            If you have a diagnosed allergy, this sits alongside the advice of your doctor and your own
            label reading. It does not replace either.
          </p>
        </div>
      </section>

      <section className="bg-slate-900">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20">
          <h2 className="font-display text-3xl font-bold tracking-tighter text-white sm:text-5xl">
            Somebody in your house is the careful one.
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-white/70">
            Give them the eleven days back.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/join"
              data-testid="about-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-slate-900 transition-colors hover:bg-slate-100"
            >
              Get recall alerts
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/pricing"
              data-testid="about-pricing-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-5 py-10">
        <div className="mx-auto max-w-6xl">
          <SourceNote />
        </div>
      </footer>
    </div>
  );
}
